import { useEffect, useRef, useState } from "react";
import { apiUrl, executeRequest, getToken } from "../../functions/requests";
import Spacer from "../../components/Spacer";
import FormInput from "../../components/FormInput";
import Button from "../../components/Button";
import IconPlus from "../../icons/IconPlus";
import SearchBar from "../../components/SearchBar";
import IconFile from "../../icons/IconFile";
import IconFolder from "../../icons/IconFolder";
import Message from "../../components/Message";

export default function KnowledgeBaseRecordCreate({
  directory = "/",
  onCreated,
}) {
  const [newRecordData, setNewRecordData] = useState({
    type: "file",
    path: directory,
    language: "html",
  });
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchingRecords, setSearchingRecords] = useState(false);
  const [searchRecordsResults, setSearchRecordsResults] = useState([]);
  const [linkedRecordsDetails, setLinkedRecordsDetails] = useState([]);

  const [message, setMessage] = useState("");
  const [messageSection, setMessageSection] = useState(null);

  const closeMessage = () => {
    setMessage("");
    setMessageSection(null);
  };

  const debounceTimeout = useRef(null); // Ref to hold timeout

  // Debounced search function
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchRecordsResults([]); // Reset results if query is empty
      return;
    }

    setSearchingRecords(true);

    // Clear the previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout for 500 ms
    debounceTimeout.current = setTimeout(async () => {
      await executeRequest(
        apiUrl + "knowledge-base-records/search",
        "POST",
        JSON.stringify({
          query: searchQuery,
          linked_records: newRecordData.linked_records,
        }),
        {
          Authorization: "Bearer " + getToken(),
          "Content-type": "application/json",
        },
        (data) => {
          console.log(data);
          setSearchRecordsResults(data);
        },
        (error) => {
          console.error(error);
        }
      );
      setSearchingRecords(false);
    }, 500);

    // Cleanup timeout on unmount or query change
    return () => clearTimeout(debounceTimeout.current);
  }, [searchQuery]);

  // Function to handle user typing
  const handleSearchInput = (query) => {
    setSearchQuery(query);
  };

  // Function to reset search input
  const handleSearchReset = () => {
    setSearchQuery("");
    setSearchRecordsResults([]);
    setSearchingRecords(false);
  };

  // Function to toggle linked records (add or remove uuid)
  const toggleLinkedRecord = (uuid, name, description, path) => {
    // Update the linkedRecordsDetails with full record details
    setLinkedRecordsDetails((prev) => {
      console.log(description);

      // Check if the uuid is already in the array
      const existingRecordIndex = prev.findIndex(
        (record) => record.uuid === uuid
      );

      let updatedLinkedRecordsDetails = [...prev];

      if (existingRecordIndex !== -1) {
        // Remove the uuid if it exists
        updatedLinkedRecordsDetails.splice(existingRecordIndex, 1);
      } else {
        // Add the uuid if it doesn't exist
        updatedLinkedRecordsDetails.push({
          uuid: uuid,
          name: name,
          description: description,
          path: path,
        });
      }

      console.log(updatedLinkedRecordsDetails);

      return updatedLinkedRecordsDetails;
    });

    setNewRecordData((prev) => {
      const updatedLinkedRecords = prev.linked_records.includes(uuid)
        ? prev.linked_records.filter((id) => id !== uuid) // Remove if exists
        : [...prev.linked_records, uuid]; // Add if not exists

      return {
        ...prev,
        linked_records: updatedLinkedRecords,
      };
    });
  };

  // Request to create new record
  const addNewRecord = async () => {
    setLoading(true);
    await executeRequest(
      apiUrl + "knowledge-base-records",
      "POST",
      JSON.stringify(newRecordData),
      {
        Authorization: "Bearer " + getToken(),
        "Content-type": "application/json",
      },
      (data) => {
        onCreated(data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  return (
    <>
      <h1 className="page-title">Create new record</h1>
      <Spacer />
      <div>Location: {directory}</div>
      <Spacer />
      <Spacer />
      <div className="flex flex-gap-l">
        <div style={{ width: "70%" }}>
          <FormInput
            value={newRecordData.name}
            type="text"
            title="Name"
            onChanged={(value) => {
              setNewRecordData((prev) => ({
                ...prev,
                name: value,
              }));
            }}
          />
          <Spacer />
          <FormInput
            value={newRecordData.type}
            type="select"
            title="Type"
            selectOptions={[
              {
                name: "File",
                uuid: "file",
              },
              {
                name: "Directory",
                uuid: "dir",
              },
            ]}
            selectAllowEmptyOption={false}
            onChanged={(value) => {
              setNewRecordData((prev) => ({
                ...prev,
                type: value,
              }));
            }}
          />
          <Spacer />
          <Spacer />
          <FormInput
            value={newRecordData.description}
            type="textarea"
            title="Description"
            onChanged={(value) => {
              setNewRecordData((prev) => ({
                ...prev,
                description: value,
              }));
            }}
          />
          <Spacer />
          {newRecordData.type && newRecordData.type === "file" && (
            <>
              <FormInput
                title="Content"
                type="rich_editor"
                language={newRecordData.content_language}
                onLanguageChanged={(value) =>
                  setNewRecordData((prev) => ({
                    ...prev,
                    content_language: value,
                  }))
                }
                value={newRecordData.content}
                onChanged={(value) => {
                  setNewRecordData((prev) => ({
                    ...prev,
                    content: value,
                  }));
                }}
              />
              <Spacer />
            </>
          )}
        </div>
        <div style={{ width: "30%" }}>
          <div>Linked records</div>
          <Spacer height="var(--spaceS)" />
          <SearchBar
            placeholder="Search records..."
            onUserTyped={handleSearchInput}
            onUserInputReset={handleSearchReset}
            isDisabled={loading}
          />
          <Spacer />
          <div>Selected:</div>
          <Spacer height="var(--spaceS)" />
          {linkedRecordsDetails.length === 0 && <div>No records selected</div>}
          {linkedRecordsDetails.length > 0 && (
            <>
              {linkedRecordsDetails.map((linkedRecord, index) => {
                return (
                  <>
                    <div
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        loading
                          ? null
                          : toggleLinkedRecord(
                              linkedRecord.uuid,
                              linkedRecord.name,
                              linkedRecord.description,
                              linkedRecord.path
                            )
                      }
                    >
                      <div className="flex flex-gap-s flex-align-center">
                        {linkedRecord.type === "dir" ? (
                          <IconFolder />
                        ) : (
                          <IconFile />
                        )}{" "}
                        {linkedRecord.name}
                      </div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{linkedRecord.description}</div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{linkedRecord.path}</div>
                    </div>
                    <Spacer height="var(--spaceS)" />
                  </>
                );
              })}
            </>
          )}
          <Spacer />
          {searchRecordsResults.length > 0 ? (
            <>
              <div>Results:</div>
              <Spacer height="var(--spaceS)" />
              {searchRecordsResults.map((searchResult, index) => {
                if (
                  linkedRecordsDetails.filter(
                    (record) => record.uuid === searchResult.uuid
                  ).length > 0
                ) {
                  return;
                }

                return (
                  <>
                    <div
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        loading
                          ? null
                          : toggleLinkedRecord(
                              searchResult.uuid,
                              searchResult.name,
                              searchResult.description,
                              searchResult.path
                            )
                      }
                    >
                      <div className="flex flex-gap-s flex-align-center">
                        {searchResult.type === "dir" ? (
                          <IconFolder />
                        ) : (
                          <IconFile />
                        )}{" "}
                        {searchResult.name}
                      </div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{searchResult.description}</div>
                      <Spacer height="var(--spaceXS)" />
                      <div>{searchResult.path}</div>
                    </div>
                    <Spacer height="var(--spaceS)" />
                  </>
                );
              })}
            </>
          ) : (
            searchQuery !== "" &&
            !searchingRecords && (
              <>
                <div>No records found.</div>
              </>
            )
          )}
          <Spacer />
          <Button
            text="Create"
            icon={<IconPlus />}
            onClicked={() => addNewRecord()}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
      {message.length > 0 && messageSection !== "" && (
        <Message
          message={message}
          type={messageSection}
          onClosed={() => closeMessage()}
        />
      )}
    </>
  );
}
